import { brightTokens, opacityScaleTokens } from '@/shared/ui/tokens';
import * as s from './SystemFrame.css';

const COLOR_PRIMARY = brightTokens.primary.default;
const COLOR_PRIMARY_DARK = brightTokens.primary.dark;
const COLOR_SECONDARY = brightTokens.secondary.default;
const COLOR_CORE = opacityScaleTokens.whiteOverlay.dense;
const CORNER_ORB_SIZE = 5;

/** 광원은 위쪽 가운데 하나 — 위쪽 장식은 빛을 받고(lit) 아래쪽은 그늘에 든다(shade). */
type Tone = 'lit' | 'shade';

interface SystemFrameProps {
  className?: string;
}

/** 고정 76×76 코너 sprite (top-left 네이티브) — 나머지 3개는 CSS rotate 로 재사용. */
const CornerSprite = ({ className, tone }: { className: string; tone: Tone }) => (
  <svg
    className={className}
    data-tone={tone}
    width="76"
    height="76"
    viewBox="0 0 76 76"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    focusable="false"
  >
    {tone === 'lit' ? (
      <>
        <path d="M0 76 L0 48 L48 0 L76 0" stroke={COLOR_PRIMARY} strokeWidth="3" fill="none" />
        <path d="M0 76 L0 48 L48 0 L76 0" stroke={COLOR_CORE} strokeWidth="1" fill="none" />
        <circle cx="24" cy="24" r={CORNER_ORB_SIZE} fill={COLOR_SECONDARY} />
        <circle cx="24" cy="24" r="2" fill={COLOR_CORE} />
        <path d="M10 38 L38 10" stroke={COLOR_SECONDARY} strokeWidth="1" strokeOpacity="0.6" />
      </>
    ) : (
      <>
        <path d="M0 76 L0 48 L48 0 L76 0" stroke={COLOR_PRIMARY_DARK} strokeWidth="3" fill="none" />
        <circle cx="24" cy="24" r="4" fill={COLOR_PRIMARY_DARK} />
        <path d="M10 38 L38 10" stroke={COLOR_PRIMARY} strokeWidth="1" strokeOpacity="0.3" />
      </>
    )}
  </svg>
);

/** 고정 16×280 세로 rune sprite — 좌/우 동일, 내부 Y 는 기존 138-band 를 0 기준 평행이동. */
const RuneSprite = ({ className }: { className: string }) => (
  <svg
    className={className}
    width="16"
    height="280"
    viewBox="0 0 16 280"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    focusable="false"
  >
    <path d="M8 0 L8 28" stroke={COLOR_PRIMARY} strokeWidth="2" strokeOpacity="0.6" />
    <circle cx="8" cy="14" r="3" fill="none" stroke={COLOR_SECONDARY} strokeWidth="1" />
    <path d="M8 65 L8 120" stroke={COLOR_PRIMARY} strokeWidth="2" strokeOpacity="0.4" />
    <path d="M8 157 L8 212" stroke={COLOR_PRIMARY} strokeWidth="2" strokeOpacity="0.4" />
    <circle cx="8" cy="185" r="3" fill="none" stroke={COLOR_SECONDARY} strokeWidth="1" />
    <path d="M8 249 L8 277" stroke={COLOR_PRIMARY} strokeWidth="2" strokeOpacity="0.6" />
  </svg>
);

/** 고정 250×16 가로 scan-bar sprite — 상/하 동일, 기존 3-dash (x115-365) 를 0 기준 평행이동. */
const ScanSprite = ({ className, tone }: { className: string; tone: Tone }) => (
  <svg
    className={className}
    data-tone={tone}
    width="250"
    height="16"
    viewBox="0 0 250 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    focusable="false"
  >
    <path d="M0 8 L77 8" stroke={COLOR_PRIMARY} strokeWidth="2" />
    <path d="M96 8 L154 8" stroke={COLOR_SECONDARY} strokeWidth="1" strokeOpacity="0.8" />
    <path d="M173 8 L250 8" stroke={COLOR_PRIMARY} strokeWidth="2" />
    {tone === 'lit' && (
      <>
        <path d="M20 8 L77 8" stroke={COLOR_CORE} strokeWidth="0.75" />
        <path d="M173 8 L230 8" stroke={COLOR_CORE} strokeWidth="0.75" />
      </>
    )}
  </svg>
);

/**
 * Size-agnostic cyber frame: CSS mask-composite gradient-border ring (고정 px
 * chamfer octagon) + 고정 크기 SVG garnish (4 코너, 좌/우 rune, 상/하 scan-bar).
 * 빛은 위쪽 가운데 한 곳에서 온다 — 링은 위가 희게 달아오르고 아래로 갈수록 깊은 파랑으로
 * 가라앉으며, 두 링 사이 베벨 띠는 위가 밝고 아래가 어둡다. 반사광이 링을 따라 한 번씩 흐르고
 * 패널 아래 바닥에 빛이 비친다.
 * className(systemFrameGlitch)은 단일 wrapper(systemFrameRoot)에 붙어 ring+garnish 가
 * 한 덩어리로 글리치한다.
 */
export const SystemFrame = ({ className }: SystemFrameProps) => (
  <div className={`${s.systemFrameRoot}${className ? ` ${className}` : ''}`} aria-hidden>
    <div className={s.frameReflection} />

    <div className={s.frameBevel} />

    {/* 각 ring 은 자체 옥타곤 clipPath 를 유지하고, 와이프는 별도 wrapper 의
        clipPath 가 담당한다 (clipPath 단일값 충돌 방지 — 옥타곤이 motion 경로에서도 살아있음).
        글로우는 wrapper 의 filter 에 건다 — ring 자신에 걸면 ring 의 mask·clipPath 가 글로우를 잘라낸다. */}
    <div className={s.revealWrapperOuter}>
      <div className={s.frameRing} />
      <div className={s.frameSheen} />
    </div>
    <div className={s.revealWrapperInner}>
      <div className={s.frameRingInner} />
    </div>

    <CornerSprite className={s.cornerTL} tone="lit" />
    <CornerSprite className={s.cornerTR} tone="lit" />
    <CornerSprite className={s.cornerBR} tone="shade" />
    <CornerSprite className={s.cornerBL} tone="shade" />

    <RuneSprite className={s.runeLeft} />
    <RuneSprite className={s.runeRight} />

    <ScanSprite className={s.scanTop} tone="lit" />
    <ScanSprite className={s.scanBottom} tone="shade" />
  </div>
);
